import React from 'react';

import { link } from '@aline/routes';
import { Skeleton, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { useIntercom } from 'react-use-intercom';
import { useBillingStyles } from 'src/components/admin/billing/SubscriptionPlan';
import { AlineButton, Spacer } from 'src/components/generic';
import { useCurrentUserQuery } from 'src/gql';
import { openLink } from 'src/helpers/url';

import { formatUsageNumber } from '../usage/usage-data';

export function AiUsage(): JSX.Element {
  const { classes } = useBillingStyles();
  const { showNewMessage } = useIntercom();
  const { currentUser, loading } = useCurrentUserQuery();
  const consumedTokens = currentUser?.aiUsage.usedTokens ?? 0;
  const totalAvailableTokens = currentUser?.aiUsage.totalAvailableTokens ?? 0;

  const aiMessage = `${formatUsageNumber(consumedTokens)}/${formatUsageNumber(
    totalAvailableTokens
  )} tokens used`;

  return (
    <div className={classes.container}>
      <Typography variant="caption">AI Usage</Typography>
      <AiUsageValue loading={loading} usage={aiMessage} />
      <AiUsageText loading={loading} resetsAt={currentUser?.aiUsage.resetsAt} />
      <Spacer />
      <div className={classes.buttonContainer}>
        <AlineButton
          color="primary"
          disabled={loading}
          variant="outlined"
          onClick={() => openLink(link.AdminUsage(), { newTab: false })}
        >
          View Usage
        </AlineButton>
        <AlineButton
          color="primary"
          disabled={loading}
          variant="contained"
          onClick={() =>
            showNewMessage('I have a question about my AI usage.  Can you help me out?')
          }
        >
          Contact Aline
        </AlineButton>
      </div>
    </div>
  );
}

interface AiUagesTextProps {
  loading: boolean;
  usage: string;
}

function AiUsageValue({ loading, usage }: AiUagesTextProps) {
  if (loading)
    return (
      <Typography variant="h2">
        <Skeleton variant="text" width={120} />
      </Typography>
    );

  return <Typography variant="h2">{usage}</Typography>;
}

function AiUsageText({ loading, resetsAt }: { loading: boolean; resetsAt?: string }) {
  const dateString = resetsAt ? format(parseISO(resetsAt), 'MMM d, yyyy') : 'an unknown date';

  if (loading)
    return (
      <div>
        <Typography>
          <Skeleton variant="text" width="40%" />
        </Typography>
        <Typography>
          <Skeleton variant="text" width="75%" />
        </Typography>
      </div>
    );

  return (
    <Typography>
      AI usage resets on {dateString}. Contact Aline to discuss your AI usage.
    </Typography>
  );
}
